// bV24 homepage — posuvníky (Swiper). Jeden konfigurační zdroj pro všechny
// sekce: [data-bv-swiper="cases|services|trust|testimonials|blog"]. Hero střídá text sám (initHeroRotator).
import Swiper from 'swiper';
import { Pagination, Autoplay, A11y, Keyboard, Mousewheel } from 'swiper/modules';

const PRESETS = {
    cases:        { slidesPerView: 'auto', spaceBetween: 42 },
    services:     { slidesPerView: 'auto', spaceBetween: 20 },
    trust:        { slidesPerView: 'auto', spaceBetween: 30, breakpoints: { 0: { spaceBetween: 20 }, 1200: { spaceBetween: 30 } } },
    testimonials: { slidesPerView: 1, spaceBetween: 30, breakpoints: { 1200: { slidesPerView: 2, spaceBetween: 230 } } },
    blog:         { slidesPerView: 'auto', spaceBetween: 80, breakpoints: { 0: { spaceBetween: 20 }, 1200: { spaceBetween: 80 } } },
    // Loga referencí: nekonečná smyčka s plynulým posunem (autoplay bez prodlevy + lineární přechod v CSS).
    logos:        { slidesPerView: 'auto', spaceBetween: 30, loop: true, speed: 3000, allowTouchMove: true, mousewheel: false,
                    autoplay: { delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true },
                    breakpoints: { 0: { spaceBetween: 20 }, 768: { spaceBetween: 30 } } },
};

export function initBvSwipers(root = document) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-bv-swiper]').forEach((el) => {
        const cfg = { ...(PRESETS[el.dataset.bvSwiper] || {}) };
        // Při omezení pohybu je autoplay vypnutý, ale nakonfigurovaný (jde spustit tlačítkem).
        if (reduce && cfg.autoplay) cfg.autoplay = { ...cfg.autoplay, enabled: false };
        const pag = el.querySelector('.swiper-pagination');
        new Swiper(el, {
            modules: [Pagination, Autoplay, A11y, Keyboard, Mousewheel],
            watchOverflow: true,
            // Posuvník skrytý na jedné šířce (desktop/mobil) se přepočítá, jakmile se ukáže.
            observer: true, observeParents: true,
            keyboard: { enabled: true, onlyInViewport: true },
            // Touchpad: posun dvěma prsty do boku. Jen vodorovná osa — svislé kolečko dál roluje stránkou.
            mousewheel: { forceToAxis: true, releaseOnEdges: true, thresholdDelta: 6 },
            a11y: { prevSlideMessage: 'Předchozí', nextSlideMessage: 'Další', paginationBulletMessage: 'Přejít na {{index}}' },
            pagination: pag ? { el: pag, clickable: true } : false,
            ...cfg,
        });
    });
}

// Video (7,8 MB) se nasadí na desktopu i mobilu, jen ne při Save-Data a „omezit pohyb"
// — pak zůstane lehký poster / mobilní snímek. Při omezení pohybu se zastaví.
// Třída is-playing na .bv-hero: mobil ukáže video až když opravdu hraje, do té doby snímek.
function initHeroVideo() {
    const video = document.querySelector('.bv-hero__video');
    if (!video || !video.dataset.src) return;
    const hero = video.closest('.bv-hero');
    const reduce  = window.matchMedia('(prefers-reduced-motion: reduce)');
    const saveData = navigator.connection && navigator.connection.saveData;
    video.addEventListener('playing', () => hero && hero.classList.add('is-playing'));
    const apply = () => {
        if (reduce.matches) { video.pause(); return; }
        if (saveData) return;
        if (!video.getAttribute('src')) video.src = video.dataset.src;
        video.play().catch(() => {});
    };
    apply();
    reduce.addEventListener('change', apply);
}

// Střídání textu v hero (prolínání přes .is-active, CSS). Bez teček a pauzy dle předlohy —
// WCAG 2.2.2 splní zastavení při najetí myší / fokusu, při „omezit pohyb" se nestřídá vůbec.
function initHeroRotator() {
    const box = document.querySelector('[data-bv-hero-rotator]');
    if (!box) return;
    const slides = [...box.querySelectorAll('.bv-hero__slide')];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let current = 0, hover = false, focus = false;
    const show = (i) => {
        slides[current].classList.remove('is-active');
        current = i % slides.length;
        slides[current].classList.add('is-active');
    };
    setInterval(() => {
        if (hover || focus || reduce.matches || document.hidden) return;
        show(current + 1);
    }, 6000);
    box.addEventListener('mouseenter', () => { hover = true; });
    box.addEventListener('mouseleave', () => { hover = false; });
    box.addEventListener('focusin', () => { focus = true; });
    box.addEventListener('focusout', () => { focus = false; });
}

// Přehled služeb: odkaz z patičky /sluzby#slug rozbalí a ukáže daný řádek (<details id="slug">).
function initAccHash() {
    const open = () => {
        const id = decodeURIComponent(window.location.hash.slice(1));
        const el = id ? document.getElementById(id) : null;
        if (el && el.tagName === 'DETAILS') { el.open = true; el.scrollIntoView({ block: 'start' }); }
    };
    open();
    window.addEventListener('hashchange', open);
}

// Karty blogu: titulek + perex mají v předloze dohromady 6 řádků (2 + 4, 4 + 2…),
// aby karty v řadě držely výšku. Počet řádků titulku zná až prohlížeč.
function initPostClamp() {
    const posts = document.querySelectorAll('.bv-post');
    if (!posts.length) return;
    const apply = () => posts.forEach((post) => {
        const title = post.querySelector('.bv-post__title');
        const text = post.querySelector('.bv-post__text');
        if (!title || !text) return;
        const lines = Math.round(title.getBoundingClientRect().height / parseFloat(getComputedStyle(title).lineHeight));
        text.style.webkitLineClamp = String(Math.max(2, 6 - lines));
    });
    apply();
    window.addEventListener('resize', apply);
    document.fonts?.ready.then(apply);
}

// „Více článků / case study" dočte další stránku výpisu na místě. Bez skriptu je to obyčejný
// odkaz na ?strana=N (vyhledávač i návštěvník bez JS projdou celý archiv).
// Výpis: [data-bv-more-grid] (přibývají jeho přímí potomci), tlačítko: [data-bv-more].
function initLoadMore() {
    const grid = document.querySelector('[data-bv-more-grid]');
    const more = document.querySelector('[data-bv-more]');
    if (!grid || !more) return;
    more.addEventListener('click', async (e) => {
        e.preventDefault();
        if (more.getAttribute('aria-busy') === 'true') return;
        more.setAttribute('aria-busy', 'true');
        try {
            const res = await fetch(more.href, { headers: { 'X-Requested-With': 'fetch' } });
            if (!res.ok) throw new Error(String(res.status));
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const cards = [...doc.querySelectorAll('[data-bv-more-grid] > *')];
            const added = cards.map((c) => grid.appendChild(document.importNode(c, true)));
            // Fokus na první nový záznam — klávesnice i odečítač pokračují tam, kde přibyl obsah.
            added[0]?.querySelector('a')?.focus();
            const next = doc.querySelector('[data-bv-more]');
            if (next) { more.href = next.getAttribute('href'); more.removeAttribute('aria-busy'); }
            else more.remove();
        } catch {
            window.location.href = more.href;   // záchrana: klasický přechod na další stránku
        }
    });
}

document.addEventListener('DOMContentLoaded', () => { initBvSwipers(); initHeroVideo(); initHeroRotator(); initPostClamp(); initAccHash(); initLoadMore(); });
